import React, { useEffect, useState } from 'react';
import { Skeleton, TablePaginationConfig } from 'antd';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation } from 'react-router';
import { useParams } from 'react-router-dom';
import { useUpdateEffect } from 'usehooks-ts';

import './index.less';

import {
  AgentCreationRequest,
  AgentModel,
  AgentState,
  AgentUpdatingRequest,
  DataInterfaceType,
} from '../../../../gamma-api';
import { defaultApi } from '../../../apis';
import { ValidationErrors } from '../../../types';
import { tryGetValidationErrors } from '../../../utils/errorUtils';
import { getTranslateFunc } from '../../../utils/stringUtil';
import { getAgentPath } from '../../../utils/pathUtils';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../utils/urlUtil';
import { ActionsEmptyLogo, AgentsEmptyLogo } from '../../../assets';
import { alertsSelectorAdd } from '../../../recoil';
import IbButton, { IIbButtonAction } from '../../../components/common/IbButton';
import IbPageTitle from '../../../components/IbPageTitle';
import IbTypography from '../../../components/common/IbTypography';
import IbIcon from '../../../components/common/IbIcon';
import IbPagination from '../../../components/common/IbPagination';
import ImportUpload from '../../../components/ImportUpload';

import {
  AGENTS_PAGE_SIZE,
  CONTENT_CLASS_NAME,
  EMPTY_CONTENT_CLASS_NAME,
  EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME,
  GRID_CLASS_NAME,
  MAIN_CLASS_NAME,
  NOTHING_FOUND_PLACEHOLDER_CLASS_NAME,
  PAGINATION_ROW_CLASS_NAME,
  SKELETON_CLASS_NAME,
  SKELETON_COLUMN_COUNT,
  SKELETON_ROW_CLASS_NAME,
  SKELETON_ROW_COUNT,
  TOOLBAR_CLASSNAME,
} from './const';
import AgentCard from './AgentCard';
import AgentModal from './AgentModal';
import ConfirmModal from './ConfirmModal';

interface IAgentsPageProps {
  interfaceType: DataInterfaceType;
}

const AgentsPage: React.FC<IAgentsPageProps> = ({ interfaceType }) => {
  const translatePrefix = interfaceType === DataInterfaceType.Api ? 'API' : 'Chat';
  const translate = getTranslateFunc(`Agent: ${translatePrefix}: List:`);

  const location = useLocation();
  const { push } = useHistory();

  const { folderId } = useParams<{ folderId: string }>();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: AGENTS_PAGE_SIZE,
    total: 0,
  });

  const [searchText, setSearchText] = useState('');
  const [agents, setAgents] = useState<AgentModel[]>([]);
  const [loading, setLoading] = useState(true);

  const [stateToggling, setStateToggling] = useState(false);
  const [deleteConfirmLoading, setDeleteConfirmLoading] = useState(false);
  const [agentModalVisible, setAgentModalVisible] = useState(false);

  const [updateAgent, setUpdateAgent] = useState<AgentModel>();
  const [deleteAgent, setDeleteAgent] = useState<AgentModel>();
  const [disableAgent, setDisableAgent] = useState<AgentModel>();

  const agentListIsEmpty = !agents.length;
  const showEmptyList = agentListIsEmpty && !loading;

  const loadAgents = async (withLoading: boolean) => {
    try {
      withLoading && setLoading(true);
      const response = await defaultApi.searchAgents(
        searchText,
        [folderId],
        interfaceType ? [interfaceType] : undefined,
        (pagination.current || 1) - 1,
        AGENTS_PAGE_SIZE,
        true
      );
      setAgents(response.data.items || []);
      setPagination((prev) => ({ ...prev, total: response.data.total_item_count || 0 }));
    } catch (err) {
      addAlert({
        content: translate('Failed to load agents'),
        type: 'error',
      });
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      withLoading && setLoading(false);
    }
  };

  const toggleAgentState = async (agent?: AgentModel) => {
    if (!agent) return;

    setStateToggling(true);
    try {
      const newState = agent.state === AgentState.Disabled ? AgentState.Enabled : AgentState.Disabled;
      await defaultApi.updateAgent(agent.agent_id, {
        name: agent.name,
        state: newState,
        settings: agent.settings,
        trace: agent.trace,
      });
      setAgents(agents.map((item) => (item.agent_id === agent.agent_id ? { ...item, state: newState } : item)));
    } catch (e) {
      addAlert({
        content: translate('Agent state toggling error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
    setStateToggling(false);
    setDisableAgent(undefined);
  };

  const goToAgent = (id: string) => {
    push(getAgentPath(interfaceType, folderId, id));
  };

  useEffect(() => {
    loadAgents(true).finally();
  }, [interfaceType, folderId, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== pagination.current) {
      setPagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [searchText]);

  useUpdateEffect(() => {
    if (pagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, pagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [pagination.current]);

  useEffect(() => {
    if (searchText) {
      setSearchText('');
    } else if (pagination.current !== 1) {
      setPagination({ ...pagination, current: 1 });
    }
  }, [interfaceType, folderId]);

  const onCreateAgentClick = () => {
    setUpdateAgent(undefined);
    setAgentModalVisible(true);
  };

  const onUpdateAgentClick = (agent: AgentModel) => () => {
    setUpdateAgent(agent);
    setAgentModalVisible(true);
  };

  const onDeleteAgentClick = (agent: AgentModel) => () => {
    setDeleteAgent(agent);
  };

  const onImportFileFinish = async (agent: AgentModel) => {
    goToAgent(agent.agent_id);
  };

  const onAgentStateToggle = (agent: AgentModel) => async () => {
    if (agent.state === AgentState.Enabled) {
      setDisableAgent(agent);
    } else {
      await toggleAgentState(agent);
    }
  };

  const onAgentModalAdd = async (
    agent: AgentCreationRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    try {
      const response = await defaultApi.createAgent(agent);
      setAgentModalVisible(false);
      goToAgent(response.data.model.agent_id);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Agent creation error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onAgentModalEdit = async (
    agent: AgentUpdatingRequest,
    onSubmitErrors: (errors: ValidationErrors) => void
  ): Promise<void> => {
    if (!updateAgent) return;

    try {
      await defaultApi.updateAgent(updateAgent.agent_id, agent);
      await loadAgents(false);
      setAgentModalVisible(false);
    } catch (e) {
      const validationErrors = tryGetValidationErrors(e as Error);

      if (validationErrors) {
        onSubmitErrors(validationErrors);
      } else {
        addAlert({
          content: translate('Agent updating error'),
          type: 'error',
          icon: <IbIcon iconName="attention" />,
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  };

  const onAgentModalCancel = () => {
    setAgentModalVisible(false);
  };

  const onDeleteConfirmButtonClick = async () => {
    if (!deleteAgent) return;

    setDeleteConfirmLoading(true);
    try {
      await defaultApi.deleteAgent(deleteAgent.agent_id);
      await loadAgents(false);
    } catch (e) {
      addAlert({
        content: translate('Agent deletion error'),
        type: 'error',
        icon: <IbIcon iconName="attention" />,
      });
    }
    setDeleteConfirmLoading(false);
    setDeleteAgent(undefined);
  };

  const onDeleteCancelButtonClick = () => {
    setDeleteAgent(undefined);
  };

  const onDisableConfirmButtonClick = async () => {
    await toggleAgentState(disableAgent);
  };

  const onDisableCancelButtonClick = () => {
    setDisableAgent(undefined);
  };

  const onPaginationChange = (page: number) => setPagination({ ...pagination, current: page });

  const onSearchTextChange = (value: string) => setSearchText(value);

  const renderToolbar = () => {
    if (showEmptyList) {
      return (
        <div className={TOOLBAR_CLASSNAME}>
          <IbButton icon={<IbIcon iconName="add-one" />} onClick={onCreateAgentClick}>
            {translate('Toolbar: Create')}
          </IbButton>
          <ImportUpload.AgentImport folderId={folderId} interfaceType={interfaceType} onFinish={onImportFileFinish}>
            <IbButton icon={<IbIcon iconName="afferent-three" />} type="secondary">
              {translate('Toolbar: Import')}
            </IbButton>
          </ImportUpload.AgentImport>
        </div>
      );
    }

    const actions: IIbButtonAction[] = [
      {
        icon: <IbIcon iconName="afferent-three" />,
        text: translate('Toolbar: Import'),
        render: (child) => (
          <ImportUpload.AgentImport folderId={folderId} interfaceType={interfaceType} onFinish={onImportFileFinish}>
            {child}
          </ImportUpload.AgentImport>
        ),
      },
    ];

    return (
      <div className={TOOLBAR_CLASSNAME}>
        <IbButton actions={actions} icon={<IbIcon iconName="add-one" />} onClick={onCreateAgentClick}>
          {translate('Toolbar: Create')}
        </IbButton>
      </div>
    );
  };

  const renderAgentList = () => (
    <>
      {renderToolbar()}
      <div className={GRID_CLASS_NAME}>
        {agents.map((agent) => (
          <AgentCard
            key={agent.agent_id}
            agent={agent}
            searchText={searchText}
            onAgentClick={() => goToAgent(agent.agent_id)}
            onAgentStateToggle={onAgentStateToggle(agent)}
            onDeleteAgent={onDeleteAgentClick(agent)}
            onEditAgent={onUpdateAgentClick(agent)}
          />
        ))}
      </div>
    </>
  );

  const renderEmptyList = () => {
    if (searchText) {
      return <div className={NOTHING_FOUND_PLACEHOLDER_CLASS_NAME}>{translate('Nothing found')}</div>;
    }

    return (
      <div className={EMPTY_CONTENT_PLACEHOLDER_CLASS_NAME}>
        {interfaceType === DataInterfaceType.Api ? <ActionsEmptyLogo /> : <AgentsEmptyLogo />}
        <IbTypography>
          <IbTypography.Paragraph disabled type="secondary">
            {translate('Empty placeholder: line 1')}
            <br />
            {translate('Empty placeholder: line 2')}
            <br />
            {translate('Empty placeholder: line 3')}
          </IbTypography.Paragraph>
        </IbTypography>
        {renderToolbar()}
      </div>
    );
  };

  // TODO: replace with 'react-loading-skeleton' or adjust animation colors for similar look.
  const renderSkeleton = () => (
    <div className={SKELETON_CLASS_NAME}>
      {Array.from({ length: SKELETON_ROW_COUNT }).map((_, row) => (
        <div key={row} className={SKELETON_ROW_CLASS_NAME}>
          {Array.from({ length: SKELETON_COLUMN_COUNT }).map((_, col) => (
            <Skeleton key={col} active />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        showSearchInput
        searchText={searchText}
        title={translate('Title')}
        onDebouncedSearch={onSearchTextChange}
      />
      <div className={`${CONTENT_CLASS_NAME} ${showEmptyList && !searchText ? EMPTY_CONTENT_CLASS_NAME : ''}`}>
        {loading ? renderSkeleton() : agentListIsEmpty ? renderEmptyList() : renderAgentList()}
      </div>
      {(pagination.total ?? 0) > AGENTS_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={AGENTS_PAGE_SIZE}
            pageSize={AGENTS_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
      <AgentModal
        editAgent={updateAgent}
        interfaceType={interfaceType}
        visible={agentModalVisible}
        onAdd={onAgentModalAdd}
        onCancel={onAgentModalCancel}
        onEdit={onAgentModalEdit}
      />
      <ConfirmModal.DeleteAgent
        interfaceType={interfaceType}
        loading={deleteConfirmLoading}
        visible={!!deleteAgent}
        onCancel={onDeleteCancelButtonClick}
        onConfirm={onDeleteConfirmButtonClick}
      />
      <ConfirmModal.DisableAgent
        interfaceType={interfaceType}
        loading={stateToggling}
        visible={!!disableAgent}
        onCancel={onDisableCancelButtonClick}
        onConfirm={onDisableConfirmButtonClick}
      />
    </div>
  );
};

export default AgentsPage;
